/**
 * Leave vocabulary: the leave types and statuses `/get-common-utils` returns
 * (`leave_types[]`, `leave_statuses[]`, each `{ id, title, description }`),
 * already loaded app-wide as `useUtils().corporateUtils`.
 *
 * Statuses are matched by lowercased title, exactly as the backend matches
 * them (`leave_state.py`), so a seed's capitalisation can't break a write.
 */

export const LEAVE_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
  CANCELLED: "cancelled",
};

/** A status title (or leave row's `status`) as a `LEAVE_STATUS` token. */
export function leaveStatusToken(title) {
  return String(title || "").trim().toLowerCase();
}

/**
 * The `leave_statuses` id for a token, or null when the lookup isn't loaded or
 * the backend has no such status — callers skip the write rather than send a
 * placeholder.
 *
 * @param {Object|null|undefined} corporateUtils
 * @param {string} token - One of `LEAVE_STATUS`.
 * @returns {string|null}
 */
export function leaveStatusIdFor(corporateUtils, token) {
  const statuses = Array.isArray(corporateUtils?.leave_statuses)
    ? corporateUtils.leave_statuses
    : [];
  return statuses.find((s) => leaveStatusToken(s?.title) === token)?.id || null;
}

/**
 * Leave types a coach may apply for, by lowercased title. The backend seeds
 * more (half-day and unpaid variants); the form offers only these for now.
 * Existing leaves of any type still display with their own type name.
 */
export const APPLICABLE_LEAVE_TYPES = ["casual", "sick"];

/**
 * Leave types for the Apply form, in the backend's order, limited to
 * `APPLICABLE_LEAVE_TYPES`.
 *
 * @param {Object|null|undefined} corporateUtils
 * @returns {Array<{ id: string, label: string, hint: string }>}
 */
export function leaveTypeOptions(corporateUtils) {
  const types = Array.isArray(corporateUtils?.leave_types) ? corporateUtils.leave_types : [];
  return types
    .filter((t) => t?.id && t?.title)
    .filter((t) => APPLICABLE_LEAVE_TYPES.includes(leaveStatusToken(t.title)))
    .map((t) => ({ id: t.id, label: t.title, hint: t.description || "" }));
}

/**
 * Status pill text and colours (Claude Design 4a/5a). Cancelled reads as
 * "Withdrawn": that is what the backend's Cancelled status means.
 *
 * @param {string} status - The leave row's `status` title.
 * @returns {{ label: string, className: string }}
 */
export function leaveStatusPill(status) {
  switch (leaveStatusToken(status)) {
    case LEAVE_STATUS.PENDING:
      return { label: "Pending", className: "text-[#9A5B00] bg-[#FFF4DB]" };
    case LEAVE_STATUS.APPROVED:
      return { label: "Approved", className: "text-[#2F8A4E] bg-[#E6F4EA]" };
    case LEAVE_STATUS.REJECTED:
      return { label: "Rejected", className: "text-[#B42318] bg-[#FDECEA]" };
    case LEAVE_STATUS.CANCELLED:
      return { label: "Withdrawn", className: "text-[#5C554F] bg-[#F4F1EE]" };
    default:
      return { label: status || "—", className: "text-[#5C554F] bg-[#F4F1EE]" };
  }
}
